import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDataCache } from '../context/DataCacheContext';
import '../styles/Page.css';
import { resolveMediaUrl } from '../utils/media';

function PortfolioDetail({ item }) {
  const { prefetchPortfolioItem, getPortfolioItem } = useDataCache();
  const [data, setData] = useState(item || {});

  // Build unified media list: images + video
  const buildMediaList = (d) => {
    const list = [];
    if (d?.media?.images) {
      d.media.images.forEach(img => list.push({ ...img, kind: 'image' }));
    }
    if (d?.media?.video?.src) {
      list.push({ src: d.media.video.src, alt: 'Video', kind: 'video', poster: d.media.video.poster });
    }
    return list;
  };

  const mediaItems = buildMediaList(data);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const hasMedia = mediaItems.length > 0;

  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Handle keyboard events for fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isFullscreen) {
        if (e.key === 'Escape') {
          setIsFullscreen(false);
        } else if (e.key === 'ArrowLeft' && mediaItems.length > 1) {
          setActiveIndex((prev) => (prev > 0 ? prev - 1 : mediaItems.length - 1));
        } else if (e.key === 'ArrowRight' && mediaItems.length > 1) {
          setActiveIndex((prev) => (prev < mediaItems.length - 1 ? prev + 1 : 0));
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, mediaItems.length]);

  // Lock scroll when lightbox open; portal renders above modal without clipping
  useEffect(() => {
    if (!isFullscreen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isFullscreen]);

  // Fetch full item by slug if list payload was minimal
  useEffect(() => {
    if (!item?.slug) return;
    
    // Check cache first
    const cached = getPortfolioItem(item.slug);
    if (cached) {
      setData(cached);
      return;
    }
    
    // Check if we need to fetch full details
    const missingDetails = !item?.meta || Object.keys(item.meta || {}).length === 0 || !item?.media || !Array.isArray(item.media.images);
    if (missingDetails) {
      // Prefetch and update when available
      prefetchPortfolioItem(item.slug).then(full => {
        if (full) {
          setData(full);
        } else {
          setData(item || {});
        }
      });
    } else {
      setData(item || {});
    }
  }, [item, prefetchPortfolioItem, getPortfolioItem]);

  return (
    <>
      <div className="portfolio-detail">
      <aside className="portfolio-info">
        <h2 className="portfolio-info-title">{data.title}</h2>
        {data.type && <div className="portfolio-type">{data.type}</div>}

        {Array.isArray(data.tags) && data.tags.length > 0 && (
          <div className="portfolio-tags">
            {data.tags.map((t) => (
              <span key={t} className="tag">{t}</span>
            ))}
          </div>
        )}

        <div className="portfolio-metrics">
          <dl>
            {data.meta?.role && (<><dt>Role</dt><dd>{data.meta.role}</dd></>)}
            {data.meta?.year && (<><dt>Year</dt><dd>{data.meta.year}</dd></>)}
            {data.meta?.engine && (<><dt>Engine</dt><dd>{data.meta.engine}</dd></>)}
            {data.meta?.software && (
              <>
                <dt>Software</dt>
                <dd>{Array.isArray(data.meta.software) ? data.meta.software.join(', ') : data.meta.software}</dd>
              </>
            )}
            {data.type === '3d-asset' && data.meta?.polycount && (<><dt>Polycount</dt><dd>{data.meta.polycount}</dd></>)}
            {data.type === '3d-asset' && data.meta?.maps && (<><dt>Maps</dt><dd>{Array.isArray(data.meta.maps) ? data.meta.maps.join(', ') : data.meta.maps}</dd></>)}
            {data.type === '3d-asset' && data.meta?.texelDensity && (<><dt>Texel Density</dt><dd>{data.meta.texelDensity}</dd></>)}
            {data.meta?.fileTypes && (<><dt>Files</dt><dd>{Array.isArray(data.meta.fileTypes) ? data.meta.fileTypes.join(', ') : data.meta.fileTypes}</dd></>)}
          </dl>
        </div>

        {/* Links (View / Download / Repo) hidden from public view.
            Backend schema + admin form still accept them for future use. */}
      </aside>

      <section className="portfolio-media">
        <div 
          className="media-viewer" 
          onClick={hasMedia && mediaItems[activeIndex].kind !== 'video' ? toggleFullscreen : undefined}
          style={{ cursor: hasMedia && mediaItems[activeIndex].kind !== 'video' ? 'pointer' : 'default' }}
          title={hasMedia && mediaItems[activeIndex].kind !== 'video' ? 'Click to enlarge (fits to screen)' : ''}
        >
          {hasMedia ? (
            mediaItems[activeIndex].kind === 'video' ? (
              <video
                key={mediaItems[activeIndex].src}
                src={resolveMediaUrl(mediaItems[activeIndex].src)}
                poster={mediaItems[activeIndex].poster ? resolveMediaUrl(mediaItems[activeIndex].poster) : undefined}
                controls
                playsInline
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            ) : (
              <img src={resolveMediaUrl(mediaItems[activeIndex].src)} alt={mediaItems[activeIndex].alt || data.title} />
            )
          ) : (
            <div className="media-placeholder">No media available</div>
          )}
        </div>
        {mediaItems.length > 1 && (
          <div className="media-thumbs" role="listbox" aria-label="Media thumbnails">
            {mediaItems.map((m, i) => (
              <button
                key={m.src || i}
                className={`thumb ${i === activeIndex ? 'active' : ''}`}
                onClick={() => setActiveIndex(i)}
                aria-selected={i === activeIndex}
              >
                {m.kind === 'video' ? (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#333', color: '#fff', fontSize: '0.7rem' }}>
                    ▶ Video
                  </div>
                ) : (
                  <img src={resolveMediaUrl(m.src)} alt={m.alt || `${data.title} ${i + 1}`} />
                )}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>

    {/* Lightbox: portal to body so it isn’t clipped by modal; z-index below fixed nav */}
    {isFullscreen &&
      createPortal(
        <div className="media-fullscreen-overlay" onClick={toggleFullscreen} role="presentation">
          <button
            type="button"
            className="fullscreen-close"
            onClick={(e) => {
              e.stopPropagation();
              toggleFullscreen();
            }}
            aria-label="Close enlarged view"
          >
            ×
          </button>

          {/* Image area — flex: 1 so it takes remaining height */}
          <div className="fullscreen-media" onClick={(e) => e.stopPropagation()}>
            {hasMedia &&
              (mediaItems[activeIndex].kind === 'video' ? (
                <video
                  key={mediaItems[activeIndex].src}
                  src={resolveMediaUrl(mediaItems[activeIndex].src)}
                  controls
                  autoPlay
                  playsInline
                  className="fullscreen-image"
                />
              ) : (
                <img
                  src={resolveMediaUrl(mediaItems[activeIndex].src)}
                  alt={mediaItems[activeIndex].alt || data.title}
                  className="fullscreen-image"
                />
              ))}

            {mediaItems.length > 1 && (
              <>
                <button
                  type="button"
                  className="fullscreen-nav fullscreen-prev"
                  onClick={() =>
                    setActiveIndex((prev) => (prev > 0 ? prev - 1 : mediaItems.length - 1))
                  }
                  aria-label="Previous media"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="fullscreen-nav fullscreen-next"
                  onClick={() =>
                    setActiveIndex((prev) => (prev < mediaItems.length - 1 ? prev + 1 : 0))
                  }
                  aria-label="Next media"
                >
                  ›
                </button>
              </>
            )}
          </div>

          {/* Thumbnail strip — sits below the image in normal flow, never overlapping */}
          {mediaItems.length > 1 && (
            <div className="fullscreen-thumbs" onClick={(e) => e.stopPropagation()}>
              {mediaItems.map((m, i) => (
                <button
                  type="button"
                  key={m.src || i}
                  className={`fullscreen-thumb ${i === activeIndex ? 'active' : ''}`}
                  onClick={() => setActiveIndex(i)}
                  aria-selected={i === activeIndex}
                >
                  {m.kind === 'video' ? (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#333',
                        color: '#fff',
                        fontSize: '0.7rem',
                      }}
                    >
                      ▶
                    </div>
                  ) : (
                    <img src={resolveMediaUrl(m.src)} alt={m.alt || `${data.title} ${i + 1}`} />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Counter badge — fixed to overlay corner */}
          {mediaItems.length > 1 && (
            <div className="fullscreen-counter" onClick={(e) => e.stopPropagation()}>
              {activeIndex + 1} / {mediaItems.length}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

function Portfolio() {
  const { getPortfolio, prefetchPortfolio, cache, prefetchPortfolioItem } = useDataCache();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeItem, setActiveItem] = useState(null);

  useEffect(() => {
    loadPortfolioData();
  }, []);

  const loadPortfolioData = async () => {
    
    setLoading(true);
    
    // Try to get from cache first
    const cachedData = await getPortfolio();

    if (cachedData && cachedData.length > 0) {
      setItems(cachedData);
      setLoading(false);
      setError('');
      return;
    }

    // If cache miss or empty, fetch fresh data
    
    const freshData = await prefetchPortfolio(true);

    // Check if there's an actual API error
    const hasApiError = cache.portfolio.error && freshData === null;
    
    if (hasApiError) {
      
      setError(cache.portfolio.error || 'Failed to load portfolio items');
      // Fallback to demo data on error (show error message)
      loadDemoData(true);
    } else if (freshData && freshData.length > 0) {
      
      setItems(freshData);
      setError('');
    } else {
      
      // Empty array is valid - just means no items in database yet
      // Use demo data without showing error (pass false to loadDemoData)
      loadDemoData(false);
    }
    
    setLoading(false);
  };

  // Throttled hover-prefetch: at most one prefetch every 300ms to avoid flooding the API
  const lastHoverPrefetch = useRef(0);
  const handleCardHover = useCallback((item) => {
    if (!item?.slug) return;
    const now = Date.now();
    if (now - lastHoverPrefetch.current < 300) return;
    lastHoverPrefetch.current = now;
    prefetchPortfolioItem(item.slug);
  }, [prefetchPortfolioItem]);

  const loadDemoData = (showError = true) => {
    const demoItems = Array.from({ length: 9 }, (_, i) => ({
      _id: i + 1,
      title: `Project ${i + 1}`,
      type: i % 2 === 0 ? '3d-asset' : 'branding',
      tags: i % 2 === 0 ? ['PBR', 'Realtime'] : ['Logo', 'Guidelines'],
      meta: i % 2 === 0
        ? {
            role: 'Modeling, Texturing',
            year: 2025,
            engine: 'Unity 2022 LTS',
            software: ['Blender', 'Substance 3D Painter'],
            polycount: '12,340 tris',
            maps: ['BaseColor', 'Normal', 'Roughness', 'AO'],
            texelDensity: '10.24 px/cm',
            fileTypes: ['FBX', 'GLB', 'PNG']
          }
        : {
            role: 'Identity, System',
            year: 2025,
            software: ['Illustrator', 'Photoshop'],
            fileTypes: ['SVG', 'PDF', 'PNG']
          },
      media: {
        images: []
      },
      links: {}
    }));
    setItems(demoItems);
    if (showError) {
      setError('API not available - showing demo data');
    } else {
      setError(''); // Clear error when API is working but returns empty
    }
  };

  const closeModal = () => setActiveItem(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    if (activeItem) {
      window.addEventListener('keydown', onKeyDown);
    }
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeItem]);

  if (loading) {
    return (
      <div className="page-container portfolio-page">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading portfolio...</p>
          <p className="loading-subtitle">This may take a moment on mobile devices</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container portfolio-page">
        <div className="error-message">
          <h3>Unable to Load Portfolio</h3>
          <p>{error}</p>
          <button 
            className="retry-button" 
            onClick={() => {
              setLoading(true);
              setError('');
              loadPortfolioData();
            }}
          >
            Try Again
          </button>
          <p className="error-help">
            If this persists, please check your internet connection or try refreshing the page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container portfolio-page">
      <div className="portfolio-grid">
        {items.map((item) => {
          const firstImage = item?.media?.images?.[0];
          const video = item?.media?.video;
          return (
            <div
              key={item._id}
              className="portfolio-card"
              onClick={() => setActiveItem(item)}
              onMouseEnter={() => handleCardHover(item)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setActiveItem(item);
              }}
              aria-label={`Open ${item.title}`}
            >
              {firstImage ? (
                <img 
                  src={resolveMediaUrl(firstImage.src)} 
                  alt={firstImage.alt || item.title}
                  className="portfolio-card-image"
                />
              ) : video?.src ? (
                <video
                  src={resolveMediaUrl(video.src)}
                  className="portfolio-card-image"
                  muted
                  loop
                  playsInline
                  autoPlay
                />
              ) : null}
              <div className="portfolio-card-overlay">
                <span className="portfolio-card-title">{item.title}</span>
              </div>
            </div>
          );
        })}
      </div>

      {activeItem && (
        <div className="portfolio-modal" onClick={closeModal}>
          <div className="portfolio-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="portfolio-modal-close" onClick={closeModal} aria-label="Close">&times;</button>
            <PortfolioDetail item={activeItem} />
          </div>
        </div>
      )}
    </div>
  );
}

export default Portfolio;
